// Department list with filters, and the detail of the selected one.

import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useGuide } from './context';
import { nodeName } from './model';
import { Card, SectionHead, Badge, EmptyNote } from './components';
import { QueueDetail } from './QueueDetail';
import { queueAvailability } from './lib/callFlowStatus';

export function DepartmentsSection({ selectedId }: { selectedId?: string }) {
  const { t, model, lang, accent, navigate, presence, presenceState } = useGuide();
  const [filter, setFilter] = useState<'all' | 'with' | 'without'>('all');
  const [q, setQ] = useState('');
  const selected = selectedId ? model.byId.get(selectedId) : undefined;

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return model.queues.filter(queue => {
      const has = (queue.agents?.length || 0) > 0 || (queue.agentGroupIds?.length || 0) > 0;
      if (filter === 'with' && !has) return false;
      if (filter === 'without' && has) return false;
      if (!s) return true;
      return nodeName(queue, lang).toLowerCase().includes(s) || queue.label.toLowerCase().includes(s);
    });
  }, [model.queues, filter, q, lang]);

  if (selected && selected.type === 'queue') {
    return (
      <div className="space-y-4">
        <button type="button" onClick={() => navigate({ section: 'departments' })} className="inline-flex items-center gap-1 text-xs text-purple-700 hover:underline print:hidden">
          <ArrowLeft className="w-3.5 h-3.5" />{t('departments')}
        </button>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-1"><Badge kind="dark">{t('callQueue')}</Badge></div>
          <h2 className="text-xl font-semibold text-gray-900">{nodeName(selected, lang)}</h2>
          <div className="text-xs text-gray-500">{selected.label}</div>
        </Card>
        <QueueDetail queue={selected} />
      </div>
    );
  }

  const chip = (value: typeof filter, label: string) => (
    <button type="button" onClick={() => setFilter(value)}
      className={`px-2.5 py-1 text-xs rounded-full ${filter === value ? 'text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
      style={filter === value ? { backgroundColor: accent } : undefined}>{label}</button>
  );

  return (
    <div className="space-y-3">
      <SectionHead title={t('departmentTitle')} intro={t('departmentIntro')} />
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        {chip('all', t('all'))}{chip('with', t('withAgents'))}{chip('without', t('withoutAgents'))}
        <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder={t('filterHere')}
          className="ml-auto px-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 w-full sm:w-56" />
      </div>
      {list.length === 0 ? <EmptyNote text={t('noResults')} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {list.map(queue => {
            const avail = queueAvailability(queue.agents, presence);
            const paths = model.pathsByTarget.get(queue.id) || [];
            return (
              <Card key={queue.id} onClick={() => navigate({ section: 'departments', id: queue.id })} className="p-3" accent={accent}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{nodeName(queue, lang)}</div>
                    <div className="text-[11px] text-gray-500 truncate">{queue.label}</div>
                  </div>
                  {avail.total > 0
                    ? (presenceState === 'ok'
                      ? <Badge kind={avail.available > 0 ? 'green' : 'red'}>{avail.available}/{avail.total}</Badge>
                      : <Badge kind="green">{avail.total} {t('agentsCount')}</Badge>)
                    : <Badge kind="red">{t('withoutAgents')}</Badge>}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {paths.slice(0, 4).map((p, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 tabular-nums">
                      {p.language === 'both' ? '' : `${p.language.toUpperCase()} `}{p.steps.map(s => s.key).join(' › ') || '—'}
                    </span>
                  ))}
                  {paths.length > 4 && <span className="text-[10px] text-gray-400">+{paths.length - 4}</span>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
